import React, { useState } from 'react';
import { useStore } from '../state/store';
import { MORE_NAV, NAV } from '../lib/constants';
import { d001Surfacing } from '../lib/operatingMode';
import { Sheet } from './Sheet';
import { ThemeSwitch } from './UserMenu';
import { UserAvatar } from './UserAvatar';
import { AskTheBridgeButton } from './TheBridge';
import EvidenceForm from './EvidenceForm';
import { CloseoutForm, ExceptionForm } from './Forms';
import MobileToday from '../pages/MobileToday';
import Actions from '../pages/Actions';
import Pipeline from '../pages/Pipeline';
import Dubai from '../pages/Dubai';
import Evidence from '../pages/Evidence';
import Runway from '../pages/Runway';
import Review from '../pages/Review';
import Strategy from '../pages/Strategy';
import Settings from '../pages/Settings';
import Gates from '../pages/Gates';
import Diagnostics from '../pages/Diagnostics';

const SHEET_PAGES = {
  actions: Actions,
  pipeline: Pipeline,
  dubai: Dubai,
  evidence: Evidence,
  runway: Runway,
  review: Review,
  strategy: Strategy,
  settings: Settings,
  gates: Gates,
  diagnostics: Diagnostics,
};

const PAGE_TITLES = Object.fromEntries([
  ...NAV.map((n) => [n.key, n.label]),
  ...MORE_NAV.map((n) => [n.key, n.label]),
]);

export function MobileShell({
  session,
  onLogout,
  page,
  go,
  modal,
  setModal,
  error,
  reload,
}) {
  const { d, state, set } = useStore();
  const [moreOpen, setMoreOpen] = useState(false);
  const theme = state.settings?.theme || 'system';
  const preSprint = d.sprint.phase === 'pre';
  const dayLabel = preSprint
    ? `PRE · T−${d.sprint.daysUntilStart}`
    : `DAY ${d.sprint.day} / 45`;
  const Page = page && page !== 'command' ? SHEET_PAGES[page] : null;
  const name = session?.displayName || session?.email || 'Signed in';
  const surfacing = d001Surfacing(d);
  const moreItems = MORE_NAV.filter((item) => {
    if (!item.d001) return true;
    return surfacing.showInNav;
  });
  const tab = modal === 'closeout'
    ? 'close'
    : moreOpen || Page
      ? 'more'
      : 'today';

  const goToday = () => {
    setMoreOpen(false);
    setModal(null);
    go('command');
  };

  const openPage = (key) => {
    setMoreOpen(false);
    go(key);
  };

  return (
    <div className="mobile-shell">
      <header className="mobile-header">
        <h1>Bridge45</h1>
        <span className="day">{dayLabel}</span>
        <div className="spacer" />
        <AskTheBridgeButton />
        {d.sprint.nextGate && (
          <button type="button" className="btn sm ghost" onClick={() => openPage('gates')}>
            Day {d.sprint.nextGate.day}
          </button>
        )}
      </header>

      {error && (
        <div className="api-error">
          <strong>A change did not reach the API.</strong>{' '}
          {error.message}
          {error.requestId ? ` (request ${error.requestId})` : ''}
          <button className="btn sm" onClick={reload}>Reload from API</button>
        </div>
      )}

      <div className="mobile-body">
        <MobileToday go={openPage} openModal={setModal} />
      </div>

      <nav className="mobile-bottom-bar" aria-label="Primary">
        <button type="button" className={`mobile-tab${tab === 'today' ? ' on' : ''}`} aria-current={tab === 'today' ? 'page' : undefined} onClick={goToday}>
          Today
        </button>
        <button
          type="button"
          className={`mobile-tab${page === 'pipeline' ? ' on' : ''}`}
          onClick={() => openPage('pipeline')}
        >
          Pipeline
        </button>
        {!preSprint && (
          <button
            type="button"
            className={`mobile-tab${tab === 'close' ? ' on' : ''}`}
            onClick={() => { setMoreOpen(false); setModal('closeout'); }}
          >
            Close
          </button>
        )}
        <button
          type="button"
          className={`mobile-tab${tab === 'more' ? ' on' : ''}`}
          aria-expanded={moreOpen || !!Page}
          onClick={() => setMoreOpen(true)}
        >
          More
        </button>
      </nav>

      {Page && (
        <Sheet
          title={PAGE_TITLES[page] || 'Bridge45'}
          onClose={goToday}
          tall
        >
          <div className="sheet-page">
            <Page go={openPage} openModal={setModal} />
          </div>
        </Sheet>
      )}

      {moreOpen && (
        <Sheet title="More" onClose={() => setMoreOpen(false)} tall>
          <div className="mobile-more-profile">
            <UserAvatar url={session?.profilePictureUrl} name={name} size={40} />
            <div>
              <div>{name}</div>
              {session?.email && <div className="dim tiny">{session.email}</div>}
            </div>
          </div>
          <div className="sheet-list">
            {NAV.filter((n) => n.key !== 'command' && n.key !== 'more').map((item) => (
              <button
                key={item.key}
                type="button"
                className="sheet-list-btn"
                onClick={() => openPage(item.key)}
              >
                {item.label}
              </button>
            ))}
            {moreItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className="sheet-list-btn"
                onClick={() => openPage(item.key)}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              className="sheet-list-btn"
              onClick={() => { setMoreOpen(false); setModal('exception'); }}
            >
              Request Strategy Exception
            </button>
          </div>
          <div style={{ marginTop: 16 }}>
            <div className="dim tiny" style={{ marginBottom: 8 }}>Appearance</div>
            <ThemeSwitch value={theme} compact onChange={(v) => set('settings.theme', v)} />
          </div>
          {onLogout && (
            <div style={{ marginTop: 16 }}>
              <button type="button" className="btn" onClick={onLogout}>Sign out</button>
            </div>
          )}
        </Sheet>
      )}

      {modal === 'evidence' && <EvidenceForm onClose={() => setModal(null)} />}
      {modal === 'closeout' && <CloseoutForm onClose={() => setModal(null)} />}
      {modal === 'exception' && <ExceptionForm onClose={() => setModal(null)} />}
    </div>
  );
}
